import React, { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'

import { FlagsProvider, defaultFlags } from '@sketch/modules-common'

import { useDevToolsFlags } from '@sketch/devtools'
import {
  GetInitialUserQuery,
  useApolloClient,
  useGetInitialUserQuery,
} from '@sketch/gql-types'

const formatArrayIntoObject = (flags: string[]) =>
  flags.reduce((acc, value) => ({ ...acc, [value]: true }), {})

const getFeatureFlagsFromData = (data?: GetInitialUserQuery) =>
  (data?.me?.featureFlags ?? []).filter(f => !!f) as string[]

const ConnectedFlagsProvider: React.FCC = ({ children }) => {
  const { data } = useGetInitialUserQuery({
    fetchPolicy: 'cache-only',
  })

  const featureFlags = getFeatureFlagsFromData(data)
  const { localFlagOverrides } = useDevToolsFlags()

  const flags = useMemo(
    () => ({
      ...defaultFlags,
      ...formatArrayIntoObject(featureFlags),
      ...localFlagOverrides,
    }),
    [featureFlags, localFlagOverrides]
  )

  useEffect(() => {
    Object.entries(flags)
      .filter(([, enabled]) => enabled)
      .forEach(([key]) => {
        Sentry.getCurrentScope().setTag(`feature.${key}`, true)
      })
  }, [flags])

  return <FlagsProvider value={flags}>{children}</FlagsProvider>
}

const SafeConnectedFlagsProvider: React.FCC = ({ children }) => {
  const client = useApolloClient()
  const [timestamp, setTimeStamp] = useState(new Date().getTime())

  useEffect(() => {
    client.onResetStore(() => {
      setTimeStamp(new Date().getTime())
      return Promise.resolve()
    })
  }, [client])

  return (
    <ConnectedFlagsProvider key={timestamp}>{children}</ConnectedFlagsProvider>
  )
}

export default SafeConnectedFlagsProvider
